/**
 * Menu 4 · Finance (harde grendel, alleen OWNER): Stripe Connect Express, Financiële Cockpit
 * (KPI-saldi, cashflow-grafiek, Transactie & Fee Ledger met verkoop- en fee-factuur-PDF per
 * transactie; medewerkers alleen als klantnummer + anoniem medewerkersnummer) en Fee & BTW
 * Overzicht (platformfee 10% uit belagom_fees_ledger, download CSV).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Download, Info, Hourglass, Landmark, AlertOctagon, AlertTriangle, AlertCircle, CheckCircle2, XCircle, Lightbulb,
  FileText, Receipt, Loader2, ShieldCheck, Table2, LineChart,
} from 'lucide-react';
import {
  getLedger, getFees, downloadFeesCsv, getBalances, getCashflow, downloadFactuurPdf,
} from '../services/opleiderCockpitApi';
import type { Markt, LedgerRegel, FinanceStatus, FactuurSoort } from '../services/opleiderCockpitApi';
import { OpleiderStripeKaart } from '../components/opleiderCockpit/OpleiderStripeKaart';
import { eur } from '../components/academy/academyStijl';
import { useAuthStore } from '../store/authStore';

export type FinanceView = 'stripe' | 'ledger' | 'fees';
const Laad: React.FC = () => <div className="p-8 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0B1F3A] mx-auto" /></div>;
const MARKT_BADGE: Record<Markt, string> = { medewerkers: 'bg-emerald-50 text-emerald-700', professionals: 'bg-blue-50 text-blue-700', bedrijven: 'bg-indigo-50 text-indigo-700' };

const Stripe: React.FC = () => {
  const { t } = useTranslation('pages');
  const { user } = useAuthStore();
  const hoofdeigenaar = (user as { opleider_is_hoofdeigenaar?: boolean } | null)?.opleider_is_hoofdeigenaar !== false;
  return (
    <div className="space-y-4 max-w-3xl">
      {!hoofdeigenaar && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <Info className="w-5 h-5 flex-shrink-0 mt-0.5" /><p>{t('opleiderCockpit.finance.stripeHoofdeigenaar')}</p>
        </div>
      )}
      <OpleiderStripeKaart alleenLezen={!hoofdeigenaar} />
      <div className="bg-white rounded-xl border border-gray-200 p-5 text-sm text-gray-700 space-y-2">
        <h3 className="font-semibold text-gray-900">{t('opleiderCockpit.finance.payoutTitel')}</h3>
        <p>{t('opleiderCockpit.finance.payoutUitleg')}</p>
        <ul className="list-disc pl-5 space-y-1 text-gray-600">
          <li>{t('opleiderCockpit.finance.matrix5')}</li>
          <li>{t('opleiderCockpit.finance.matrix10')}</li>
          <li>{t('opleiderCockpit.finance.matrix85')}</li>
        </ul>
      </div>
    </div>
  );
};

// ─── Laag 1: KPI-saldi ───────────────────────────────────────────────────────

const KpiKaart: React.FC<{ icon: React.ReactNode; titel: string; bedrag: number; sub: string; extra?: React.ReactNode; accent: string }> = ({ icon, titel, bedrag, sub, extra, accent }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col gap-2">
    <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${accent}`}>{icon}</span>
      {titel}
    </div>
    <p className="text-3xl font-semibold text-gray-900 tabular-nums">{eur.format(bedrag)}</p>
    <p className="text-xs text-gray-500">{sub}</p>
    {extra}
  </div>
);

const Saldi: React.FC = () => {
  const { t } = useTranslation('pages');
  const { data } = useQuery({ queryKey: ['opleider-finance-balances'], queryFn: getBalances });
  if (!data) return <div className="grid gap-4 md:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="h-36 rounded-xl bg-gray-100 animate-pulse" />)}</div>;
  const maandLabel = new Date(`${data.maand}-01T12:00:00`).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
  return (
    <div className="grid gap-4 md:grid-cols-3">
      <KpiKaart
        icon={<Hourglass className="w-4 h-4" />} accent="bg-blue-50 text-blue-700"
        titel={t('opleiderCockpit.finance.kpi.omzetTitel')} bedrag={data.omzet_lopende_maand}
        sub={t('opleiderCockpit.finance.kpi.omzetSub', { maand: maandLabel, n: data.transacties_lopende_maand })}
        extra={<p className="text-xs text-gray-600">{t('opleiderCockpit.finance.kpi.omzetNetto', { bedrag: eur.format(data.omzet_lopende_maand_netto) })}</p>}
      />
      <KpiKaart
        icon={<Landmark className="w-4 h-4" />} accent="bg-emerald-50 text-emerald-700"
        titel={t('opleiderCockpit.finance.kpi.beschikbaarTitel')} bedrag={data.beschikbaar_voor_uitbetaling}
        sub={t('opleiderCockpit.finance.kpi.beschikbaarSub', { n: data.beschikbaar_aantal })}
      />
      <KpiKaart
        icon={<AlertOctagon className="w-4 h-4" />} accent="bg-rose-50 text-rose-700"
        titel={t('opleiderCockpit.finance.kpi.openstaandTitel')} bedrag={data.openstaand_saldo}
        sub={t('opleiderCockpit.finance.kpi.openstaandSub', { n: data.openstaand_aantal })}
        extra={data.verstreken_aantal > 0 ? (
          <p className="text-xs font-medium text-rose-700 inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />{t('opleiderCockpit.finance.kpi.verstreken', { bedrag: eur.format(data.verstreken_saldo), n: data.verstreken_aantal })}</p>
        ) : undefined}
      />
    </div>
  );
};

// ─── Laag 2: Cashflow-lijngrafiek ────────────────────────────────────────────

/** Gevalideerd categorisch paar (dataviz-validator: CVD ΔE 20,7, contrast ≥ 3:1). */
const SERIES = [
  { key: 'bruto' as const, kleur: '#2563eb' },
  { key: 'netto' as const, kleur: '#0d9488' },
];
const H = 260;
const M = { top: 16, right: 112, bottom: 28, left: 64 };

function useBreedte<T extends HTMLElement>(): [React.RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [b, setB] = useState(640);
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([e]) => setB(Math.max(320, Math.round(e.contentRect.width))));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, b];
}

function mooieMax(v: number): number {
  if (v <= 0) return 100;
  const macht = 10 ** Math.floor(Math.log10(v));
  const stap = [1, 2, 2.5, 5, 10].find((s) => s * macht >= v / 4)! * macht;
  return Math.ceil(v / stap) * stap;
}

const maandKort = (m: string) => new Date(`${m}-01T12:00:00`).toLocaleDateString('nl-NL', { month: 'short' }).replace('.', '');
const eurKort = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

const Cashflow: React.FC = () => {
  const { t } = useTranslation('pages');
  const { data } = useQuery({ queryKey: ['opleider-finance-cashflow'], queryFn: () => getCashflow(12) });
  const [ref, breedte] = useBreedte<HTMLDivElement>();
  const [actief, setActief] = useState<number | null>(null);
  const [tabel, setTabel] = useState(false);
  const labels = { bruto: t('opleiderCockpit.finance.cashflow.bruto'), netto: t('opleiderCockpit.finance.cashflow.netto') };

  const reeks = data ?? [];
  const w = breedte - M.left - M.right;
  const ih = H - M.top - M.bottom;
  const max = mooieMax(Math.max(0, ...reeks.map((m) => m.bruto)));
  const x = (i: number) => M.left + (reeks.length <= 1 ? w / 2 : (i / (reeks.length - 1)) * w);
  const y = (v: number) => M.top + ih - (v / max) * ih;
  const pad = (k: 'bruto' | 'netto') => reeks.map((m, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(m[k]).toFixed(1)}`).join('');
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);
  const leeg = reeks.every((m) => m.aantal === 0);

  const kiesUitPointer = (e: React.PointerEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - r.left;
    if (reeks.length === 0) return;
    const i = Math.round(((px - M.left) / w) * (reeks.length - 1));
    setActief(Math.max(0, Math.min(reeks.length - 1, i)));
  };
  const toets = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); setActief((a) => Math.min(reeks.length - 1, (a ?? -1) + 1)); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); setActief((a) => Math.max(0, (a ?? reeks.length) - 1)); }
    if (e.key === 'Escape') setActief(null);
  };
  const tip = actief != null ? reeks[actief] : null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-semibold text-gray-900">{t('opleiderCockpit.finance.cashflow.titel')}</h3>
          <p className="text-xs text-gray-500">{t('opleiderCockpit.finance.cashflow.sub')}</p>
        </div>
        <div className="flex items-center gap-4">
          <ul className="flex items-center gap-4 text-xs text-gray-700" aria-label={t('opleiderCockpit.finance.cashflow.legenda')}>
            {SERIES.map((s) => (
              <li key={s.key} className="inline-flex items-center gap-1.5"><span className="w-4 h-0.5 rounded-full" style={{ background: s.kleur }} />{labels[s.key]}</li>
            ))}
          </ul>
          <button type="button" onClick={() => setTabel((v) => !v)} className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg px-2.5 py-1 hover:bg-gray-50">
            {tabel ? <LineChart className="w-3.5 h-3.5" /> : <Table2 className="w-3.5 h-3.5" />}
            {tabel ? t('opleiderCockpit.finance.cashflow.grafiek') : t('opleiderCockpit.finance.cashflow.tabel')}
          </button>
        </div>
      </div>

      {!data ? <div className="h-[260px] rounded-lg bg-gray-50 animate-pulse" /> : tabel ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-gray-500">
              <tr><th className="py-2 pr-4">{t('opleiderCockpit.finance.maand')}</th><th className="py-2 pr-4 text-right">{labels.bruto}</th><th className="py-2 pr-4 text-right">{labels.netto}</th><th className="py-2 text-right">{t('opleiderCockpit.finance.transacties')}</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {reeks.map((m) => (
                <tr key={m.maand}><td className="py-2 pr-4 text-gray-700">{m.maand}</td><td className="py-2 pr-4 text-right tabular-nums">{eur.format(m.bruto)}</td><td className="py-2 pr-4 text-right tabular-nums">{eur.format(m.netto)}</td><td className="py-2 text-right text-gray-500">{m.aantal}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div ref={ref} className="relative">
          <svg
            width={breedte} height={H} className="block touch-none select-none outline-none focus-visible:ring-2 focus-visible:ring-[#0B1F3A] rounded"
            role="img" tabIndex={0} aria-label={t('opleiderCockpit.finance.cashflow.aria')}
            onPointerMove={kiesUitPointer} onPointerLeave={() => setActief(null)} onKeyDown={toets} onBlur={() => setActief(null)}
          >
            {ticks.map((v) => (
              <g key={v}>
                <line x1={M.left} x2={M.left + w} y1={y(v)} y2={y(v)} stroke="#e5e7eb" strokeDasharray={v === 0 ? undefined : '3 4'} />
                <text x={M.left - 8} y={y(v)} dy="0.32em" textAnchor="end" className="fill-gray-500" fontSize={11}>{eurKort.format(v)}</text>
              </g>
            ))}
            {reeks.map((m, i) => (
              <text key={m.maand} x={x(i)} y={H - 8} textAnchor="middle" className={actief === i ? 'fill-gray-900' : 'fill-gray-500'} fontSize={11}>{maandKort(m.maand)}</text>
            ))}
            {tip && <line x1={x(actief!)} x2={x(actief!)} y1={M.top} y2={M.top + ih} stroke="#9ca3af" strokeWidth={1} />}
            {SERIES.map((s) => <path key={s.key} d={pad(s.key)} fill="none" stroke={s.kleur} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />)}
            {/* Directe labels aan het einde van iedere lijn */}
            {reeks.length > 0 && SERIES.map((s, si) => {
              const laatste = reeks[reeks.length - 1][s.key];
              const anderY = y(reeks[reeks.length - 1][SERIES[1 - si].key]);
              const ly = y(laatste) + (Math.abs(y(laatste) - anderY) < 14 ? (si === 0 ? -8 : 8) : 0);
              return <text key={s.key} x={x(reeks.length - 1) + 8} y={ly} dy="0.32em" fontSize={11} className="fill-gray-700">{labels[s.key]}</text>;
            })}
            {tip && SERIES.map((s) => <circle key={s.key} cx={x(actief!)} cy={y(tip[s.key])} r={4.5} fill={s.kleur} stroke="#fff" strokeWidth={2} />)}
          </svg>
          {leeg && <p className="absolute inset-x-0 top-1/2 -translate-y-1/2 text-center text-sm text-gray-500 pointer-events-none">{t('opleiderCockpit.finance.cashflow.leeg')}</p>}
          {tip && (
            <div
              className="absolute z-10 pointer-events-none bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs min-w-[11rem]"
              style={{ top: M.top, left: x(actief!) > breedte / 2 ? x(actief!) - 12 : x(actief!) + 12, transform: x(actief!) > breedte / 2 ? 'translateX(-100%)' : undefined }}
            >
              <p className="font-medium text-gray-900 mb-1">{new Date(`${tip.maand}-01T12:00:00`).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })}</p>
              {SERIES.map((s) => (
                <p key={s.key} className="flex items-center justify-between gap-3">
                  <span className="inline-flex items-center gap-1.5 text-gray-600"><span className="w-2.5 h-2.5 rounded-full" style={{ background: s.kleur }} />{labels[s.key]}</span>
                  <strong className="tabular-nums text-gray-900">{eur.format(tip[s.key])}</strong>
                </p>
              ))}
              <p className="mt-1 text-gray-500">{t('opleiderCockpit.finance.cashflow.transacties', { n: tip.aantal })}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Laag 3: Transactie & Fee Ledger ─────────────────────────────────────────

const STATUS_STIJL: Record<FinanceStatus, { cls: string; icon: React.ReactNode }> = {
  gegenereerd: { cls: 'text-amber-700', icon: <Lightbulb className="w-3.5 h-3.5" /> },
  voldaan: { cls: 'text-emerald-600', icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
  verstreken: { cls: 'text-rose-700/80', icon: <AlertCircle className="w-3.5 h-3.5" /> },
  geannuleerd: { cls: 'text-gray-500', icon: <XCircle className="w-3.5 h-3.5" /> },
  gecrediteerd: { cls: 'text-gray-500', icon: <XCircle className="w-3.5 h-3.5" /> },
};

const Referentie: React.FC<{ r: LedgerRegel }> = ({ r }) => {
  const { t } = useTranslation('pages');
  if (r.markt === 'medewerkers') {
    return <span className="font-mono">{t('opleiderCockpit.finance.ref.klant', { nr: r.klantnummer ?? '–' })} | {t('opleiderCockpit.finance.ref.transactie', { nr: r.medewerker_referentie ?? '–' })}</span>;
  }
  if (r.markt === 'bedrijven') return <span>{t('opleiderCockpit.finance.ref.opdrachtgever', { naam: r.debiteur ?? '–' })}{r.klantnummer && <span className="font-mono"> · {r.klantnummer}</span>}</span>;
  return <span>{t('opleiderCockpit.finance.ref.professional', { naam: r.debiteur ?? '–' })}</span>;
};

const PdfKnop: React.FC<{ r: LedgerRegel; soort: FactuurSoort; label: string; icon: React.ReactNode }> = ({ r, soort, label, icon }) => {
  const { t } = useTranslation('pages');
  const [bezig, setBezig] = useState(false);
  const klik = async () => {
    setBezig(true);
    try { await downloadFactuurPdf(r, soort); } catch { toast.error(t('opleiderCockpit.finance.pdfFout')); } finally { setBezig(false); }
  };
  return (
    <button type="button" onClick={klik} disabled={bezig} className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-[#0B1F3A] border border-[#0B1F3A]/20 bg-white rounded-md px-2 py-1 hover:bg-blue-50 disabled:opacity-50">
      {bezig ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : icon}{label}
    </button>
  );
};

const Ledger: React.FC = () => {
  const { t } = useTranslation('pages');
  const [markt, setMarkt] = useState<Markt | 'alle'>('alle');
  const { data, isLoading } = useQuery({ queryKey: ['opleider-ledger'], queryFn: getLedger });
  if (isLoading || !data) return <Laad />;
  const regels = data.regels.filter((r) => markt === 'alle' || r.markt === markt);
  const som = (k: 'activeringswaarde' | 'netto') => regels.filter((r) => r.status !== 'geannuleerd' && r.status !== 'gecrediteerd').reduce((a, r) => a + r[k], 0);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-semibold text-gray-900 mr-2">{t('opleiderCockpit.finance.ledgerTitel')}</h3>
        {(['alle', 'medewerkers', 'professionals', 'bedrijven'] as const).map((m) => (
          <button key={m} type="button" onClick={() => setMarkt(m)} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${markt === m ? 'bg-[#0B1F3A] text-white border-[#0B1F3A]' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
            {m === 'alle' ? t('opleiderCockpit.signals.alle') : t(`opleiderCockpit.markt.${m}`)}
          </button>
        ))}
        <p className="ml-auto text-xs text-gray-500 inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" />{t('opleiderCockpit.finance.ledgerAvg')}</p>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        {regels.length === 0 ? <p className="p-10 text-center text-gray-500">{t('opleiderCockpit.geenData')}</p> : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">{t('opleiderCockpit.finance.kolom.factuur')}</th>
                <th className="px-4 py-3">{t('opleiderCockpit.finance.kolom.opleiding')}</th>
                <th className="px-4 py-3 text-right">{t('opleiderCockpit.finance.kolom.activering')}</th>
                <th className="px-4 py-3 text-right">{t('opleiderCockpit.finance.kolom.netto')}</th>
                <th className="px-4 py-3">{t('opleiderCockpit.finance.kolom.status')}</th>
                <th className="px-4 py-3">{t('opleiderCockpit.finance.kolom.downloads')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {regels.map((r) => {
                const st = STATUS_STIJL[r.status];
                return (
                  <tr key={r.id} className="hover:bg-gray-50 align-top">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <p className="font-mono text-xs text-gray-900">#{r.verkoop_factuurnummer}</p>
                      <p className="text-xs text-gray-500">{new Date(r.datum).toLocaleDateString('nl-NL')}</p>
                      {r.crediteert_factuurnummer && <p className="text-[11px] text-gray-500">{t('opleiderCockpit.finance.crediteert', { nr: r.crediteert_factuurnummer })}</p>}
                      {r.creditnota_nummer && <p className="text-[11px] text-gray-500">{t('opleiderCockpit.finance.gecrediteerdMet', { nr: r.creditnota_nummer })}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-900 max-w-[18rem] truncate" title={r.omschrijving}>{r.omschrijving}</p>
                      <p className="text-xs text-gray-500"><Referentie r={r} /></p>
                      <span className={`mt-1 inline-block text-[11px] px-1.5 py-0.5 rounded-full ${MARKT_BADGE[r.markt]}`}>{t(`opleiderCockpit.markt.${r.markt}`)}</span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                      {eur.format(r.activeringswaarde)}
                      <p className="text-[11px] text-gray-400">{t('opleiderCockpit.finance.vanMarktprijs', { bedrag: eur.format(r.marktprijs) })}</p>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-gray-900">
                      {eur.format(r.netto)}
                      {r.uitbetaald && <p className="text-[11px] font-normal text-emerald-600">{t('opleiderCockpit.finance.uitbetaald')}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium ${st.cls}`} title={t(`opleiderCockpit.finance.statusUitleg.${r.status}`)}>
                        {st.icon}{t(`opleiderCockpit.finance.fs.${r.status}`)}
                      </span>
                      {r.status !== 'voldaan' && r.status !== 'geannuleerd' && r.status !== 'gecrediteerd' && r.vervaldatum && (
                        <p className="text-[11px] text-gray-400">{t('opleiderCockpit.finance.vervalt', { datum: new Date(r.vervaldatum).toLocaleDateString('nl-NL') })}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {r.pdf ? (
                        <div className="flex flex-col gap-1.5 items-start">
                          <PdfKnop r={r} soort="verkoopfactuur" label={t('opleiderCockpit.finance.pdfVerkoop')} icon={<FileText className="w-3.5 h-3.5" />} />
                          <PdfKnop r={r} soort="fee-factuur" label={t('opleiderCockpit.finance.pdfFee')} icon={<Receipt className="w-3.5 h-3.5" />} />
                        </div>
                      ) : <p className="text-[11px] text-gray-400 max-w-[10rem]">{t('opleiderCockpit.finance.pdfLegacy')}</p>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 text-sm font-semibold text-gray-900">
              <tr>
                <td className="px-4 py-3" colSpan={2}>{t('opleiderCockpit.finance.totaal', { n: regels.length })}</td>
                <td className="px-4 py-3 text-right tabular-nums">{eur.format(som('activeringswaarde'))}</td>
                <td className="px-4 py-3 text-right tabular-nums">{eur.format(som('netto'))}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
};

/** Financiële Cockpit: KPI-saldi → cashflow → transactie & fee ledger. */
const Overzicht: React.FC = () => (
  <div className="space-y-6">
    <Saldi />
    <Cashflow />
    <Ledger />
  </div>
);

const Fees: React.FC = () => {
  const { t } = useTranslation('pages');
  const { data, isLoading } = useQuery({ queryKey: ['opleider-fees'], queryFn: getFees });
  if (isLoading || !data) return <Laad />;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-600 max-w-2xl">{t('opleiderCockpit.finance.feesUitleg', { fee: data.platformfee_pct, btw: data.btw_pct })}</p>
        <button type="button" onClick={() => downloadFeesCsv()} className="inline-flex items-center gap-2 bg-[#0B1F3A] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1A3459]"><Download className="w-4 h-4" />{t('opleiderCockpit.finance.downloadCsv')}</button>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        {data.maanden.length === 0 ? <p className="p-10 text-center text-gray-500">{t('opleiderCockpit.geenData')}</p> : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">{t('opleiderCockpit.finance.maand')}</th>
                <th className="px-4 py-3 text-right">{t('opleiderCockpit.finance.transacties')}</th>
                <th className="px-4 py-3 text-right">{t('opleiderCockpit.finance.bruto')}</th>
                <th className="px-4 py-3 text-right">{t('opleiderCockpit.finance.feeExcl')}</th>
                <th className="px-4 py-3 text-right">{t('opleiderCockpit.finance.btw', { btw: data.btw_pct })}</th>
                <th className="px-4 py-3 text-right">{t('opleiderCockpit.finance.feeIncl')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.maanden.map((m) => (
                <tr key={m.maand} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{m.maand}</td>
                  <td className="px-4 py-3 text-right text-gray-600">
                    {m.aantal}
                    {m.creditnotas > 0 && <span className="block text-[11px] text-gray-400">{t('opleiderCockpit.finance.creditnotasMaand', { n: m.creditnotas })}</span>}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-600">{eur.format(m.bruto)}</td>
                  <td className="px-4 py-3 text-right text-gray-900">{eur.format(m.fee)}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{eur.format(m.btw)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-gray-900">{eur.format(m.fee_incl_btw)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export const OpleiderFinancePage: React.FC<{ view: FinanceView }> = ({ view }) => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
    {view === 'stripe' && <Stripe />}
    {view === 'ledger' && <Overzicht />}
    {view === 'fees' && <Fees />}
  </div>
);

export default OpleiderFinancePage;
